import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { researchToolsApi } from '../api';
import type { GuestSurveyResponse } from '../types';
import '../survey-hub.css';

/** Guest respondent form — template respondentForm() / public survey shell. */
export const GuestSurveyView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [survey, setSurvey] = useState<GuestSurveyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedTime, setSubmittedTime] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    researchToolsApi
      .getGuestSurvey(token)
      .then((res) => {
        setSurvey(res);
        if (res.mode === 'ANONYMOUS') setIsVerified(true);
      })
      .catch(() => setError('Опрос не найден или закрыт для прохождения.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSendVerification = async () => {
    if (!token || !inputEmail.trim()) {
      alert('Укажите корректный адрес электронной почты');
      return;
    }
    setVerifying(true);
    try {
      const res = await researchToolsApi.verifyGuestCode(token, inputEmail);
      setVerificationToken(res.verification_token);
      if (res.verified) {
        setIsVerified(true);
        setVerifiedEmail(res.email);
        setInputCode(res.dev_code || '');
        alert(res.detail || `Адрес подтверждён: ${inputEmail}`);
      } else {
        setInputCode(res.dev_code || '');
        alert(
          res.dev_code
            ? `Код отправлен на ${inputEmail}. Код для проверки: ${res.dev_code}`
            : `Код подтверждения отправлен на ${inputEmail}`,
        );
      }
    } catch {
      alert('Ошибка при отправке кода подтверждения');
    } finally {
      setVerifying(false);
    }
  };

  const handleConfirmCode = async () => {
    if (!token || !inputCode.trim()) {
      alert('Введите код подтверждения');
      return;
    }
    try {
      const res = await researchToolsApi.confirmGuestCode(token, inputEmail, inputCode.trim());
      setVerificationToken(res.verification_token);
      setIsVerified(true);
      setVerifiedEmail(res.email);
    } catch {
      alert('Неверный или просроченный код');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !survey) return;

    for (const q of survey.questions) {
      if (q.is_required) {
        const key = q.id || String(q.order_index);
        const ans = answers[key];
        if (ans === undefined || ans === '' || (Array.isArray(ans) && ans.length === 0)) {
          alert(`Ответьте на обязательный вопрос ${q.order_index}: «${q.title}»`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const res = await researchToolsApi.submitGuestSurvey(token, {
        answers,
        is_verified: survey.mode === 'VERIFIED',
        verified_email: survey.mode === 'VERIFIED' ? verifiedEmail : undefined,
        verification_token: verificationToken || undefined,
      });
      setSubmittedTime(res.submitted_at);
    } catch {
      alert('Ошибка при сохранении ответа');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="survey-hub-public-page">
        <p style={{ textAlign: 'center', padding: '4rem 1rem' }}>Загрузка формы опроса…</p>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="survey-hub-public-page">
        <section className="survey-hub-panel">
          <div className="survey-hub-panel-body" style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>{error || 'Опрос недоступен'}</h2>
          </div>
        </section>
      </div>
    );
  }

  if (submittedTime) {
    return (
      <div className="survey-hub-public-page">
        <div className="survey-hub-preview-shell">
          <div className="survey-hub-preview-brand">
            <small>Zertteu Academy · исследовательский опрос</small>
            <h2>Спасибо! Ответ сохранён</h2>
            <p style={{ margin: '7px 0 0', opacity: 0.76 }}>
              {survey.title} · {new Date(submittedTime).toLocaleString('ru-RU')}
            </p>
          </div>
          <div className="survey-hub-preview-body">
            <p>Результаты переданы в исследование. Регистрация в кабинете не требуется.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-hub-public-page">
      <div className="survey-hub-preview-shell">
        <div className="survey-hub-preview-brand">
          <small>Zertteu Academy · исследовательский опрос</small>
          <h2>{survey.title}</h2>
          <p style={{ margin: '7px 0 0', opacity: 0.76 }}>
            {survey.mode === 'VERIFIED'
              ? 'Ответ с подтверждением · e-mail подтверждён'
              : 'Анонимный ответ · e-mail не требуется'}
            {survey.audience ? ` · ${survey.audience}` : ''}
            {survey.language ? ` · ${survey.language}` : ''}
          </p>
        </div>

        <div className="survey-hub-preview-body">
          {survey.description ? <p>{survey.description}</p> : null}

          {survey.mode === 'VERIFIED' && !isVerified ? (
            <div className="survey-hub-form" style={{ marginTop: 12 }}>
              <div className="survey-hub-field full">
                <label>Электронная почта *</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    value={inputEmail}
                    onChange={(e) => setInputEmail(e.target.value)}
                    placeholder="name@university.kz"
                    style={{ flex: 1, minWidth: 200 }}
                  />
                  <button
                    type="button"
                    className="survey-hub-btn primary"
                    disabled={verifying || !inputEmail.trim()}
                    onClick={() => void handleSendVerification()}
                  >
                    {verifying ? 'Отправка…' : 'Выслать код'}
                  </button>
                </div>
              </div>
              {verificationToken ? (
                <div className="survey-hub-field full">
                  <label>Код из письма *</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value)}
                      placeholder="Код подтверждения"
                      style={{ flex: 1, minWidth: 160 }}
                    />
                    <button
                      type="button"
                      className="survey-hub-btn primary"
                      onClick={() => void handleConfirmCode()}
                    >
                      Подтвердить и открыть форму
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)}>
              {isVerified && survey.mode === 'VERIFIED' ? (
                <p style={{ color: '#49ddb7', fontSize: 12 }}>
                  Подтверждено: {verifiedEmail}
                </p>
              ) : null}

              {survey.questions.map((q, idx) => {
                const qKey = q.id || String(q.order_index);
                let input: React.ReactNode = null;

                if (q.question_type === 'text') {
                  input = (
                    <textarea
                      rows={3}
                      value={(answers[qKey] as string) || ''}
                      onChange={(e) => setAnswers({ ...answers, [qKey]: e.target.value })}
                    />
                  );
                } else if (q.question_type === 'date') {
                  input = (
                    <input
                      type="date"
                      value={(answers[qKey] as string) || ''}
                      onChange={(e) => setAnswers({ ...answers, [qKey]: e.target.value })}
                    />
                  );
                } else if (q.question_type === 'scale') {
                  input = (
                    <div className="survey-hub-radio">
                      {[1, 2, 3, 4, 5].map((v) => (
                        <span key={v}>
                          <input
                            type="radio"
                            name={`q${qKey}`}
                            checked={answers[qKey] === v}
                            onChange={() => setAnswers({ ...answers, [qKey]: v })}
                          />{' '}
                          {v}
                        </span>
                      ))}
                    </div>
                  );
                } else if (q.question_type === 'multi') {
                  const opts = q.options || [];
                  const current = (answers[qKey] as string[]) || [];
                  input = (
                    <div className="survey-hub-radio">
                      {opts.map((opt) => (
                        <span key={opt}>
                          <input
                            type="checkbox"
                            checked={current.includes(opt)}
                            onChange={() => {
                              const next = current.includes(opt)
                                ? current.filter((x) => x !== opt)
                                : [...current, opt];
                              setAnswers({ ...answers, [qKey]: next });
                            }}
                          />{' '}
                          {opt}
                        </span>
                      ))}
                    </div>
                  );
                } else if (q.question_type === 'matrix') {
                  input = (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="survey-hub-map-table">
                        <thead>
                          <tr>
                            <th>Критерий</th>
                            {[1, 2, 3, 4, 5].map((c) => (
                              <th key={c}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(q.matrix_rows || []).map((row) => (
                            <tr key={row}>
                              <td>{row}</td>
                              {[1, 2, 3, 4, 5].map((c) => (
                                <td key={c}>
                                  <input
                                    type="radio"
                                    name={`mat_${qKey}_${row}`}
                                    checked={
                                      (answers[qKey] as Record<string, string> | undefined)?.[
                                        row
                                      ] === String(c)
                                    }
                                    onChange={() => {
                                      const prev =
                                        (answers[qKey] as Record<string, string>) || {};
                                      setAnswers({
                                        ...answers,
                                        [qKey]: { ...prev, [row]: String(c) },
                                      });
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                } else {
                  const opts = q.options || ['Да', 'Нет'];
                  input = (
                    <div className="survey-hub-radio">
                      {opts.map((opt) => (
                        <span key={opt}>
                          <input
                            type="radio"
                            name={`q${qKey}`}
                            checked={answers[qKey] === opt}
                            onChange={() => setAnswers({ ...answers, [qKey]: opt })}
                          />{' '}
                          {opt}
                        </span>
                      ))}
                    </div>
                  );
                }

                return (
                  <div key={qKey} className="survey-hub-rq">
                    <label>
                      {idx + 1}. {q.title}
                      {q.is_required ? ' *' : ''}
                    </label>
                    {q.prompt ? <small style={{ color: '#5d6c78' }}>{q.prompt}</small> : null}
                    {input}
                  </div>
                );
              })}

              <button
                className="survey-hub-btn primary"
                type="submit"
                style={{ marginTop: 18 }}
                disabled={submitting}
              >
                {submitting ? 'Отправка…' : 'Отправить ответы'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
